import {
  Form,
  useLoaderData,
  useNavigation,
  useActionData,
  useNavigate,
} from "react-router-dom";
import { LoaderData } from "./loader";
import { PageNavigationHeader } from "@/features/private-room/contracts/list/layouts/page-navigation-header";
import { DEFAULT_ROUTES } from "@/constants/routes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  getContractTypeLabel,
  getNextBirthDate,
  getRenewalIndicatorLabel,
  getStatusData,
} from "@/features/private-room/contracts/list/utils/private-office-contract";
import { formatInTimeZone } from "date-fns-tz";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";
import { formatCurrency } from "@/utils/format/currency";
import {
  calculateAmountCharged,
  calculateMarginPercentage,
  calculateTransferAmount,
} from "@/features/private-room/contracts/list/utils/private-office-contract/index";
import {
  addDays,
  differenceInDays,
  formatDistanceToNow,
  startOfMonth,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CheckCircle,
  ExternalLink,
  Home,
  Link2Off,
  Loader,
  SirenIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/inputs/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectValue,
  SelectTrigger,
  SelectItem,
} from "@/components/ui/select";
import { parseWithZod } from "@conform-to/zod";
import { getInputProps, useForm } from "@conform-to/react";
import { schema, Action } from "./action";
import { useState, useEffect } from "react";
import { CycleDetails } from "@/features/private-room/bookings/layouts/cycle-details";
import { PackageDetails } from "@/features/private-room/bookings/layouts/package-details";
import { useGetPackages } from "@/hooks/use-get-packages/use-get-packages";
import { PoContractUpdateSimulation } from "@/features/private-room/bookings/layouts/po-contracts-update-simulation";
import { usePoContractsUpdatePreview } from "@/hooks/use-po-contracts-update-preview/use-po-contracts-update-preview";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio";
import { Checkbox } from "@/components/ui/checkbox";
import { useDownsell } from "@/hooks/use-downsell/use-downsell";
import { DownsellCyclesTable } from "@/features/private-room/bookings/layouts/updated-cycles-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FullScreenSheet,
  FullScreenSheetContent,
} from "@/components/full-screen-sheet/full-screen-sheet";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

export default function PrivateOfficeContractsCancel() {
  const { contract, contractId, cancellationReasonOptions, maxDate } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const actionData = useActionData() as Action;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingSheetOpen, setIsLoadingSheetOpen] = useState(false);
  const [cancelationMonthYear, setCancelationMonthYear] = useState<string>("");
  const [cancellationDateError, setcancellationDateError] = useState<
    string | null
  >(null);
  const [selectedBooking, setSelectedBooking] = useState<{
    id: number;
    bookingDate: string;
  }>();
  const [customPeriod, setCustomPeriod] = useState(false);
  const [calculatedNoticeDays, setCalculatedNoticeDays] = useState<number>();

  useEffect(() => {
    if (isLoadingSheetOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isLoadingSheetOpen]);

  const isActionRunning = navigation.state === "submitting";

  const [form, fields] = useForm({
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema });

      if (formData.get("cancellationDate") && cancellationDateError) {
        return {
          ...result,
          status: "error",
          payload: result.payload,
          error: { cancellationDate: [cancellationDateError] },
          reply: result.reply,
        };
      }
      return result;
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
    defaultValue: {
      noticeInDays: contract?.noticeInDaysCompany,
    },
  });

  const { getPackageDetails, packageDetails, getActiveCycle, activeCycle } =
    useGetPackages();

  const {
    simulatedDownsell,
    handleBookingDownsellSimulation,
    handleCreateDownsell,
    downsellLoadingStatus,
    downsellSuccess,
    downsellErrorMessage,
  } = useDownsell();

  const {
    simulatedUpdate: { toCreate, toUpdate, toCancel },
    simulateUpdate,
  } = usePoContractsUpdatePreview();

  const statusData = getStatusData(contract?.status || "");

  const pricePerCredit = contract?.currentCycle?.credits?.unitPrice || 0;
  const totalAmountCharged = calculateAmountCharged(
    Number(contract?.monthlyCredits),
    pricePerCredit,
  );
  const totalTransferAmount = calculateTransferAmount(
    contract?.takePercentage || "",
    contract?.partnerPrice.toString() || "",
  );
  const totalMarginPercentage = calculateMarginPercentage(
    totalAmountCharged,
    totalTransferAmount,
  );

  const calculateNoticeDates = (date: string, noticeFrom: "start" | "end") => {
    const noticeInDays = Number(form.value?.noticeInDays) || 0;
    const startOfCurrentMonth = startOfMonth(new Date());

    if (noticeFrom === "start") {
      const calculatedEndDate = addDays(date, noticeInDays);
      const formattedEndDate = formatInTimeZone(
        calculatedEndDate,
        "UTC",
        "yyyy-MM-dd",
      );

      setCalculatedNoticeDays(noticeInDays);

      getActiveCycle(
        formattedEndDate,
        contract?.packageId,
        contract?.company?.id,
      );

      form.update({
        name: fields.cancellationDate.name,
        value: formattedEndDate,
      });
    }

    if (noticeFrom === "end") {
      const calculatedNoticePeriod = Math.min(
        differenceInDays(
          date,
          formatInTimeZone(startOfCurrentMonth, "UTC", "yyyy-MM-dd"),
        ) + 1,
        noticeInDays,
      );

      setCalculatedNoticeDays(calculatedNoticePeriod);

      if (noticeInDays > 0) {
        const calculatedStartDate = subDays(date, calculatedNoticePeriod - 1);
        const formattedStartDate = formatInTimeZone(
          calculatedStartDate,
          "UTC",
          "yyyy-MM-dd",
        );

        form.update({
          name: fields.noticeStartDate.name,
          value: formattedStartDate,
        });
      }

      getActiveCycle(date, contract?.packageId, contract?.company?.id);
    }
  };

  const handleNoticeInDaysChange = () => {
    if (form.value?.noticeStartDate) {
      calculateNoticeDates(form.value?.noticeStartDate, "start");
    }
  };

  useEffect(() => {
    getPackageDetails(contract?.packageId, contract?.company?.id);
  }, []);

  useEffect(() => {
    if (form.value?.cancellationDate) {
      setCancelationMonthYear(
        formatInTimeZone(form.value?.cancellationDate, "UTC", "yyyy-MM"),
      );

      simulateUpdate({
        contractId: contract?.id,
        alteredFields: {
          endDate: form.value?.cancellationDate,
        },
      });
    }
  }, [form.value?.cancellationDate]);

  useEffect(() => {
    if (!activeCycle && !form.value?.cancellationDate) {
      return;
    }

    if (!activeCycle && form.value?.cancellationDate) {
      setcancellationDateError(
        "Não foi possível encontrar um ciclo ativo para a data selecionada",
      );
    } else {
      setcancellationDateError(null);
    }
  }, [activeCycle, form.value?.cancellationDate]);

  useEffect(() => {
    const booking = contract.bookings.find(
      (booking) =>
        new Date(booking.bookingDate) >=
        new Date(form.value?.cancellationDate || ""),
    );

    if (booking) {
      setSelectedBooking({ id: booking.id, bookingDate: booking.bookingDate });
    }
  }, [form.value?.cancellationDate]);

  useEffect(() => {
    if (form.value?.flowType && form.value?.cancellationDate) {
      if (form.value?.flowType === "downsell") {
        handleBookingDownsellSimulation({
          companyId: contract.companyId,
          packageId: contract.packageId,
          bookingId:
            selectedBooking?.id ||
            contract.bookings[contract.bookings.length - 1].id,
          cancelStrategy: "only-cycles",
          poContractId: Number(contractId),
          cancelledAt: formatInTimeZone(
            form.value?.cancellationDate,
            "UTC",
            "yyyy-MM-dd",
          ),
        });
      }
    }
  }, [form.value?.flowType, form.value?.cancellationDate]);

  useEffect(() => {
    if (
      actionData?.status === "success" &&
      form.value?.flowType === "downsell"
    ) {
      handleCreateDownsell({
        companyId: contract.companyId,
        contractId: Number(contractId),
        packageId: contract.packageId,
        bookingId:
          selectedBooking?.id ||
          contract.bookings[contract.bookings.length - 1].id,
        cancelStrategy: "only-cycles",
      });
    }
  }, [actionData?.status]);

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="p-6 max-w-7xl mx-auto gap-6 flex flex-col">
        <PageNavigationHeader
          onClick={() => navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST)}
          title="Editando contrato de venda de escritório"
          subtitle={
            <>
              Associe um escritório privativo à uma empresa contrante.{" "}
              <span className="font-medium">
                Campos com asterísco * são obrigatórios.
              </span>
            </>
          }
        />
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="self-stretch text-foreground text-2xl font-medium">
              Detalhes do contrato
            </CardTitle>
            <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
              Revise os dados do contrato antes de seguir para o cancelamento.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="border-r border-gray-200">
                <div className="text-sm text-gray-500 mb-1">ID do contrato</div>
                <div className="font-medium">#{contract?.id}</div>
              </div>
              <div className="border-r border-gray-200">
                <div className="text-sm text-gray-500 mb-1">Créditos</div>
                <div className="font-medium">
                  {contract?.monthlyCredits?.toFixed(2)}
                </div>
              </div>
              <div className="border-r border-gray-200">
                <div className="text-sm text-gray-500 mb-1">Num. Pessoas</div>
                <div className="font-medium">{contract?.members}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Status</div>
                <Badge
                  style={{
                    backgroundColor: statusData.bgColor,
                  }}
                  className="text-gray-800 hover:bg-[${statusData.bgColor}]/80"
                >
                  {statusData.label}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-6">
              <div className="gap-6 flex flex-col">
                <h3 className="text-foreground font-medium text-lg bg-gray-50 rounded-lg p-4">
                  Informações gerais
                </h3>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">Empresa:</div>
                  <div className="font-medium">{contract?.company?.name}</div>
                </div>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">Espaço:</div>
                  <div className="font-medium">{contract?.space?.name}</div>
                </div>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">Sala:</div>
                  <div className="font-medium">{contract?.spot?.label}</div>
                </div>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Tipo de reserva
                  </div>
                  <div className="font-medium">
                    {getContractTypeLabel(contract?.type)}
                  </div>
                </div>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Data de início :
                  </div>
                  <div className="font-medium">
                    {safeFormatInTimeZone(
                      contract.startDate,
                      "UTC",
                      "dd/MM/yyyy",
                    )}
                  </div>
                </div>
                <div className="px-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Data de término:
                  </div>
                  <div className="font-medium">
                    {safeFormatInTimeZone(
                      contract?.endDate,
                      "UTC",
                      "dd/MM/yyyy",
                      "Indeterminado",
                    )}
                  </div>
                </div>
              </div>
              <div className="gap-6 flex flex-col">
                <h3 className="text-foreground font-medium text-lg bg-gray-50 rounded-lg p-4">
                  Financeiro
                </h3>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Créditos mensais:
                  </div>
                  <div className="font-medium">
                    {contract?.monthlyCredits?.toFixed(2)}
                  </div>
                </div>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Valor cobrado:
                  </div>
                  <div className="font-medium">
                    {totalAmountCharged
                      ? formatCurrency(totalAmountCharged)
                      : "-"}
                  </div>
                </div>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">
                    Valor do parceiro:
                  </div>
                  <div className="font-medium">
                    {formatCurrency(Number(contract?.partnerPrice))}
                  </div>
                </div>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">Take:</div>
                  <div className="font-medium">{contract?.takePercentage}%</div>
                </div>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">Repasse:</div>
                  <div className="font-medium">
                    {totalTransferAmount
                      ? formatCurrency(totalTransferAmount)
                      : "-"}
                  </div>
                </div>
                <div className="pl-4">
                  <div className="text-sm text-gray-500 mb-1">Margem:</div>
                  <div className="font-medium">
                    {totalMarginPercentage ? `${totalMarginPercentage}%` : "-"}
                  </div>
                </div>
              </div>
              <div className="gap-6 flex flex-col">
                <h3 className="text-foreground font-medium text-lg bg-gray-50 rounded-lg p-4">
                  Detalhes adicionais
                </h3>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    ID do pacote:
                  </span>
                  <br />
                  <span className="font-medium">#{contract?.packageId}</span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Data aniversário:
                  </span>
                  <br />
                  <span className="font-medium">
                    {contract?.startDate
                      ? getNextBirthDate(contract?.startDate)
                      : "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Tempo de uso do escritório:
                  </span>
                  <br />
                  <span className="font-medium">
                    {contract?.startDate
                      ? formatDistanceToNow(new Date(contract.startDate), {
                          locale: ptBR,
                        })
                      : "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Vigência prevista:
                  </span>
                  <br />
                  <span className="font-medium">
                    {contract?.expectedDurationMonths
                      ? `${contract?.expectedDurationMonths} meses`
                      : "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Aviso prévio cliente:
                  </span>
                  <br />
                  <span className="font-medium">
                    {contract?.noticeInDaysCompany
                      ? `${contract?.noticeInDaysCompany} dias`
                      : "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Aviso prévio parceiro:
                  </span>
                  <br />
                  <span className="font-medium">
                    {contract?.noticeInDaysPartner
                      ? `${contract?.noticeInDaysPartner} dias`
                      : "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500 mb-1">
                    Índice de reajuste
                  </span>
                  <br />
                  <span className="font-medium">
                    {getRenewalIndicatorLabel(contract?.renewalIndicator || "")}
                  </span>
                </div>
              </div>
              <div className="gap-6 flex flex-col">
                <h3 className="text-foreground font-medium text-lg bg-gray-50 rounded-lg p-4">
                  Espaço
                </h3>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">ID do espaço:</span>
                  <br />
                  <span className="font-medium">#{contract?.space?.id}</span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">Espaço</span>
                  <br />
                  <a
                    href={`${import.meta.env.VITE_PORTAL_URL}/coworking/space/${contract?.space?.vanishName}`}
                    target="_blank"
                    className="flex items-center gap-1"
                    rel="noreferrer"
                  >
                    <span className="font-medium underline cursor-pointer">
                      {contract?.space?.name} - #{contract?.space?.id}
                    </span>
                    <ExternalLink className="w-4 h-4 cursor-pointer" />
                  </a>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">
                    ID do escritório:
                  </span>
                  <br />
                  <span className="font-medium">#{contract?.spot?.id}</span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">Endereço:</span>
                  <br />
                  <span className="font-medium">
                    {contract?.space?.address || "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">CM Responsável:</span>
                  <br />
                  <span className="font-medium">
                    {contract?.space?.cmName || "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">CM E-mail:</span>
                  <br />
                  <span className="font-medium">
                    {contract?.space?.cmEmail || "-"}
                  </span>
                </div>
                <div className="pl-4">
                  <span className="text-sm text-gray-500">CM Telefone:</span>
                  <br />
                  <span className="font-medium">
                    {contract?.space?.cmPhone || "-"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Form
          className="flex flex-col gap-6"
          method="POST"
          id={form.id}
          onSubmit={form.onSubmit}
        >
          <Card>
            <CardHeader>
              <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                Informações de cancelamento
              </CardTitle>
              <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                Para prosseguir com o cancelamento informe os dados abaixo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Card className="mb-6">
                <CardHeader className="pb-1">
                  <CardTitle className="text-foreground font-medium text-xl">
                    Descubra o melhor prazo para encerrar o contrato
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                    Escolha uma data de início e fim e veja automaticamente
                    quando o aviso prévio deve começar e terminar.
                  </CardDescription>
                </CardContent>
                <CardContent>
                  <div className="w-full border-t pt-4 flex flex-col gap-6">
                    <div className="grid grid-cols-5 gap-4">
                      <div>
                        <Input
                          label="Período de Aviso Prévio *"
                          showlabel={true}
                          {...getInputProps(fields.noticeInDays, {
                            type: "number",
                          })}
                          onWheel={(e) => e.currentTarget.blur()}
                          error={fields.noticeInDays.errors}
                          readOnly={!customPeriod}
                          className={
                            !customPeriod
                              ? "bg-gray-100 cursor-not-allowed"
                              : ""
                          }
                          onBlur={() => {
                            handleNoticeInDaysChange();
                          }}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Checkbox
                            id="custom-period"
                            className="border-gray-300"
                            checked={customPeriod}
                            onCheckedChange={(checked) => {
                              setCustomPeriod(checked as boolean);
                              if (!checked) {
                                form.update({
                                  name: fields.noticeInDays.name,
                                  value: contract?.noticeInDaysCompany,
                                });

                                handleNoticeInDaysChange();
                              }
                            }}
                          />
                          <Label
                            htmlFor="custom-period"
                            className="text-xs font-normal text-muted-foreground cursor-pointer"
                          >
                            Período diferente do contrato
                          </Label>
                        </div>
                      </div>
                      <div>
                        <Input
                          label="Quando quer iniciar o aviso? *"
                          showlabel={true}
                          alternativeCalendarIcon
                          disable={!form.value?.noticeInDays}
                          {...getInputProps(fields.noticeStartDate, {
                            type: "date",
                          })}
                          min={formatInTimeZone(
                            startOfMonth(new Date()),
                            "UTC",
                            "yyyy-MM-dd",
                          )}
                          max={maxDate || ""}
                          onBlur={(e) => {
                            calculateNoticeDates(e.target.value, "start");
                          }}
                          error={fields.noticeStartDate?.errors}
                        />
                      </div>
                      <div>
                        <Input
                          label="Data final *"
                          showlabel={true}
                          alternativeCalendarIcon
                          disable={!form.value?.noticeInDays}
                          {...getInputProps(fields.cancellationDate, {
                            type: "date",
                          })}
                          min={formatInTimeZone(
                            startOfMonth(new Date()),
                            "UTC",
                            "yyyy-MM-dd",
                          )}
                          max={maxDate || ""}
                          onBlur={(e) => {
                            calculateNoticeDates(e.target.value, "end");
                          }}
                          error={
                            cancellationDateError ||
                            fields.cancellationDate?.errors
                          }
                        />
                      </div>
                      <div>
                        <Input
                          label="Dias de aviso prévio (calculado)"
                          showlabel={true}
                          type="text"
                          placeholder="0 dias"
                          disable={true}
                          value={
                            form.value?.cancellationDate &&
                            Math.max(calculatedNoticeDays || 0, 0)
                          }
                        />
                      </div>
                      <div>
                        <Label>Motivo do cancelamento *</Label>
                        <div className="flex flex-col gap-1 mt-1">
                          <Select
                            key={fields.cancellationReason?.id}
                            name={fields.cancellationReason?.name}
                            onValueChange={() => {
                              form.validate();
                            }}
                          >
                            <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                              <SelectValue placeholder="Selecione um motivo" />
                            </SelectTrigger>
                            <SelectContent>
                              {cancellationReasonOptions.map(
                                (cancellationReason) => (
                                  <SelectItem
                                    key={cancellationReason.value}
                                    value={cancellationReason.value}
                                  >
                                    {cancellationReason.label}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        {form.value?.cancellationReason === "Outros" && (
                          <Input
                            className="mt-1"
                            label="Outros"
                            placeholder="Descreva o motivo..."
                            {...getInputProps(fields.otherReason, {
                              type: "text",
                            })}
                          />
                        )}
                        {fields.otherReason?.errors && (
                          <p className="text-sm text-red-500 mt-1">
                            {fields.otherReason?.errors}
                          </p>
                        )}
                        {fields.cancellationReason?.errors && (
                          <p className="text-sm text-red-500 mt-1">
                            {fields.cancellationReason?.errors}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {Number(calculatedNoticeDays) <
                    contract?.noticeInDaysCompany && (
                    <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-full gap-1 inline-flex mt-4">
                      <div className="flex gap-3  items-center pl-6">
                        <div className="absolute top-4 left-4">
                          <SirenIcon
                            className="text-yellow-900"
                            width={16}
                            height={16}
                          />
                        </div>
                        <div>
                          <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                            Atenção
                          </AlertTitle>
                          <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                            O período de aviso prévio (
                            {Math.max(calculatedNoticeDays || 0, 0)} dias) é
                            menor que o período mínimo (
                            {contract?.noticeInDaysCompany} dias). Considere
                            ajustar a data final do contrato.
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  )}
                </CardContent>
              </Card>
              <div className="flex flex-col gap-6">
                {(packageDetails || activeCycle) && (
                  <Card className="flex bg-neutral-100">
                    {packageDetails?.groups && (
                      <div className="flex-1">
                        <PackageDetails
                          selectedPackage={packageDetails}
                          packageGroups={packageDetails.groups}
                          className="border-r-2"
                        />
                      </div>
                    )}
                    {activeCycle && (
                      <div className="flex-1">
                        <CycleDetails activeCycle={activeCycle} />
                      </div>
                    )}
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
          {!!toCreate.count ||
            !!toUpdate.count ||
            (!!toCancel.count && (
              <Card>
                <CardHeader>
                  <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                    Bookings afetados
                  </CardTitle>
                  <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                    Como os próximos bookings ficarão após o cancelamento.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-muted-foreground">
                        Todos os bookings a partir de{" "}
                        <span className="font-medium text-black">
                          {formatInTimeZone(
                            new Date(cancelationMonthYear),
                            "UTC",
                            "MM/yyyy",
                          )}
                        </span>{" "}
                        serão afetados.
                      </p>
                    </div>
                  </div>
                  <PoContractUpdateSimulation
                    toCreate={toCreate}
                    toUpdate={toUpdate}
                    toCancel={toCancel}
                  />
                </CardContent>
              </Card>
            ))}
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                Escolha de que forma será feito o cancelamento.
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Você pode cancelar apenas a reserva do mês atual, mantendo as
                demais, ou todas as reservas futuras. Também é possível editar
                os ciclos afetados pelo cancelamento.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <RadioGroup
                name={fields.flowType.name}
                value={fields.flowType.value}
                onValueChange={(value) => {
                  form.update({ name: fields.flowType.name, value });
                }}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem id="flow_type_cancel" value="cancel" />
                  <Label htmlFor="flow_type_cancel" className="cursor-pointer">
                    Cancelar contrato sem editar ciclos.
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="flow_type_downsell"
                    value="downsell"
                    disabled={packageDetails?.duration !== "monthly"}
                  />
                  <Label
                    htmlFor="flow_type_downsell"
                    className={`cursor-pointer ${packageDetails?.duration !== "monthly" ? "text-gray-400 cursor-not-allowed" : ""}`}
                  >
                    Cancelar ciclos e ajustar créditos.
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
          {simulatedDownsell && (
            <Card>
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Ciclos Afetados
                </CardTitle>
                <CardDescription>
                  Revise abaixo como os ciclos ao longo da reserva serão
                  impactados.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {simulatedDownsell && (
                  <DownsellCyclesTable data={simulatedDownsell} />
                )}
              </CardContent>
            </Card>
          )}
          <div className="mt-6 flex justify-end">
            <Button
              variant="destructive"
              className="px-4 py-2"
              disabled={
                !form.valid || isActionRunning || !!cancellationDateError
              }
              onClick={(e) => {
                e.preventDefault();
                form.validate();
                if (form.valid) {
                  setIsModalOpen(true);
                }
              }}
            >
              Cancelar contrato
            </Button>
          </div>
        </Form>
      </div>

      {isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-foreground font-medium">
                Confirmar cancelamento?
              </DialogTitle>
              <DialogDescription>
                Após cancelado, essa ação não poderá ser desfeita. Deseja
                continuar?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={isActionRunning}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                disabled={isActionRunning}
                onClick={() => {
                  setIsModalOpen(false);
                  scrollTo({
                    top: 0,
                    behavior: "instant",
                  });
                  setIsLoadingSheetOpen(true);

                  const formElement = document.getElementById(
                    form.id,
                  ) as HTMLFormElement;
                  if (formElement) {
                    formElement.requestSubmit();
                  }
                }}
              >
                {actionData?.status === "error"
                  ? "Fechar"
                  : "Cancelar contrato"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isLoadingSheetOpen && (
        <FullScreenSheet
          isOpen={isLoadingSheetOpen}
          onOpenChange={setIsLoadingSheetOpen}
        >
          <FullScreenSheetContent
            className="flex flex-col gap-4 items-center justify-center"
            hasCloseButton={false}
          >
            {(downsellLoadingStatus || isActionRunning) && (
              <div className="flex flex-col gap-1 items-center justify-center">
                <Loader className="h-16 w-16 animate-spin" />
                <h2 className="text-foreground text-xl font-medium mt-4">
                  {downsellLoadingStatus
                    ? "Realizando downsell de pacotes..."
                    : "Cancelando o contrato..."}
                </h2>
                <p className="text-muted-foreground text-sm mt-1 text-center">
                  Este processo pode levar alguns instantes. <br />
                  Por favor, mantenha esta página aberta e não desligue seu
                  computador.
                </p>
              </div>
            )}
            {((actionData?.status === "success" &&
              form.value?.flowType === "cancel") ||
              (downsellSuccess === true &&
                form.value?.flowType === "downsell")) &&
              !isActionRunning &&
              !downsellLoadingStatus && (
                <div className="flex flex-col gap-1 items-center justify-center">
                  <CheckCircle className="h-16 w-16" />
                  <h2 className="text-foreground text-xl font-medium mt-4">
                    Sucesso!
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    O contrato foi cancelado com sucesso.
                  </p>
                </div>
              )}
            {(downsellSuccess === false || actionData?.status === "error") && (
              <div className="flex flex-col gap-1 items-center justify-center">
                <Link2Off className="h-16 w-16" />
                <h2 className="text-foreground text-xl font-medium mt-4">
                  {downsellSuccess === false
                    ? "Algo deu errado ao realizar downsell de pacotes"
                    : "Algo deu errado ao cancelar o contrato"}
                </h2>
                {actionData?.status === "error" && (
                  <Card className="bg-neutral-100 text-black max-w-md p-4 my-4 rounded-md m-2">
                    <p className="text-red-500 text-sm mt-1 text-center">
                      {actionData?.errorMessage
                        ? actionData.errorMessage
                        : "Ocorreu um erro durante o processo.<br/>Por favor, verifique sua conexão e tente novamente mais tarde."}
                    </p>
                  </Card>
                )}
                {downsellSuccess === false && downsellErrorMessage && (
                  <Card className="bg-neutral-100 text-black max-w-md p-4 my-4 rounded-md m-2">
                    <p className="text-red-500 text-sm mt-1 text-center">
                      {downsellErrorMessage}
                    </p>
                  </Card>
                )}
              </div>
            )}
            {!isActionRunning && !downsellLoadingStatus && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() =>
                  navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST)
                }
              >
                <Home className="w-4 h-4" />
                Retornar ao início
              </Button>
            )}
          </FullScreenSheetContent>
        </FullScreenSheet>
      )}
    </div>
  );
}
